import { useMemo, useState } from "react";
import { eventStatus, isEndingSoon, formatDateRange, daysUntilEnd, formatDistance } from "@oshimap/utils";
import { normalizeCategoryCode , distinctPlaceCount } from "@oshimap/domain";
import { CATEGORIES } from "@oshimap/config";
import { ROUTES } from "@oshimap/config";
import { ep, session } from "../../lib/api";
import { useQuery } from "../../lib/use-query";
import { navigate } from "../../app/router";
import { Button, CategoryChip, Skeleton, EmptyState, ErrorState, VerificationBadge, CategoryIcon, EventIcon } from "../../components/ui";
import "./home.css";
import { queryKeys } from "@oshimap/query";

/**
 * FR-HOME-01: 홈은 KPI 대시보드가 아니라 Editorial Feed 다.
 * 섹션마다 비어 있을 때의 대체 콘텐츠를 갖는다 (FR-HOME-08).
 */
export function HomeScreen() {
  const events = useQuery(queryKeys.events(), (signal) => ep.listEvents({}, { signal }));
  const places = useQuery(queryKeys.places({ pageSize: 12 }), (signal) => ep.listPlaces({ pageSize: 12 }, { signal }));
  /* FR-HOME-04: 위치가 있으면 실제 주변을, 없으면 목록으로 대체한다.
     권한 거부는 막다른 길이 아니다 (FR-ENT-04). */
  const [coords, setCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locState, setLocState] = useState<"idle" | "asking" | "granted" | "denied">("idle");
  const nearby = useQuery(
    coords && session.state.status === "authenticated"
      ? queryKeys.mapNearby(coords, 3000)
      : ["map", "nearby", "skip", session.state.status],
    async (signal) => (coords && session.state.status === "authenticated"
      ? ep.nearbyPlaces(coords, 3000, {}, { signal })
      : null),
  );
  const askLocation = () => {
    if (!navigator.geolocation) { setLocState("denied"); return; }
    setLocState("asking");
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ latitude: p.coords.latitude, longitude: p.coords.longitude }); setLocState("granted"); },
      () => setLocState("denied"),
      { timeout: 8000, maximumAge: 60_000 },
    );
  };

  const now = useMemo(() => new Date(), []);
  const ongoing = useMemo(
    () => events.state.status === "success" ? events.state.data.filter((e) => eventStatus(e, now) === "ONGOING") : [],
    [events.state, now],
  );

  /** 진행 중인 이벤트가 열리는 **서로 다른 장소** 수. 규칙은 domain 에 있다 (Native 와 공유). */
  const ongoingPlaces = useMemo(() => distinctPlaceCount(ongoing), [ongoing]);
  const nearbyList = useMemo(
    () => (nearby.state.status === "success" && nearby.state.data ? nearby.state.data.places.slice(0, 6) : []),
    [nearby.state],
  );
  const endingSoon = useMemo(
    () => ongoing.filter((e) => isEndingSoon(e, 7, now)).sort((a, b) => (daysUntilEnd(a, now) ?? 99) - (daysUntilEnd(b, now) ?? 99)),
    [ongoing, now],
  );

  return (
    <div className="om-screen om-home">
      <header className="om-home__head">
        <p className="om-eyebrow">이번 주</p>
        {/*
          * "곳" 이라고 썼으니 **장소**를 세야 한다. 진행 중인 이벤트 수를 세면
          * 한 장소에서 두 이벤트가 열릴 때 두 곳으로 잡힌다.
          * 못 불러왔을 때 "0곳" 이라고 단언하지도 않는다 — 없다고 말하면 발길을 돌린다.
          */}
        <h1 className="om-home__title">
          {events.state.status === "error" ? "갈 만한 곳"
          : events.state.status === "success" ? <>갈 만한 곳 <em className="om-home__count num">{ongoingPlaces}</em>곳</>
          : "갈 만한 곳 찾는 중…"}
        </h1>
      </header>

      <Section
        title="이번 주 이벤트"
        more={ongoing.length > 0 ? { label: "모두 보기", to: ROUTES.events } : undefined}
        state={events.state.status}
        error={events.state.status === "error" ? events.state.error : undefined}
        onRetry={events.reload}
        empty={<EmptyState level={3} title="이번 주는 조용하네요" body="다음 주에 열리는 이벤트를 미리 볼까요?"
          action={<Button variant="secondary" size="sm" onClick={() => navigate(ROUTES.explore)}>탐색으로</Button>} />}
        isEmpty={ongoing.length === 0}
      >
        <div className="om-hscroll">
          {ongoing.slice(0, 6).map((e) => (
            <button key={e.id} className="om-ticket" onClick={() => navigate(ROUTES.event(e.id))}>
              <span className="om-ticket__art"><EventIcon size={34} /></span>
              <span className="om-ticket__stub">
                <span className="om-ticket__name">{e.name}</span>
                <span className="om-ticket__meta num">{formatDateRange(e.startsAt, e.endsAt)}</span>
                <span className="om-ticket__meta">
                  <VerificationBadge status={e.verificationStatus} />
                </span>
              </span>
            </button>
          ))}
        </div>
      </Section>

      <Section
        title="곧 종료"
        state={events.state.status}
        onRetry={events.reload}
        isEmpty={endingSoon.length === 0}
        empty={null}
      >
        <div className="om-hscroll">
          {endingSoon.slice(0, 6).map((e) => {
            const d = daysUntilEnd(e, now);
            return (
              <button key={e.id} className="om-ticket" onClick={() => navigate(ROUTES.event(e.id))}>
                <span className="om-ticket__art" aria-hidden="true">
                  <EventIcon size={34} />
                  {d !== null ? <span className="om-sticker">D-{Math.max(0, d)}</span> : null}
                </span>
                <span className="om-ticket__stub">
                  <span className="om-ticket__name">{e.name}</span>
                  <span className="om-ticket__meta num">{formatDateRange(e.startsAt, e.endsAt)}</span>
                </span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section
        title={locState === "granted" ? "내 주변" : "둘러보기"}
        more={{ label: "지도로", to: ROUTES.map }}
        state={places.state.status}
        error={places.state.status === "error" ? places.state.error : undefined}
        onRetry={places.reload}
        isEmpty={places.state.status === "success" && places.state.data.length === 0}
        empty={<EmptyState level={3} title="아직 등록된 곳이 없어요" body="알고 계신 곳을 제보해 주시면 지도에 올라갑니다." />}
      >
        {locState !== "granted" ? (
          <p className="om-locnote">
            {locState === "denied"
              ? "위치를 쓸 수 없어 전체 목록을 보여드려요. 지도에서 지역을 골라도 됩니다."
              : "위치를 켜면 지금 갈 수 있는 곳부터 보여드려요."}
            {locState !== "denied" ? (
              <button className="om-more" onClick={askLocation} disabled={locState === "asking"}>
                {locState === "asking" ? "확인 중…" : "내 주변 보기"}
              </button>
            ) : null}
          </p>
        ) : null}
        <div className="om-cards">
          {nearbyList.length > 0 ? nearbyList.map((p) => (
            <button key={p.id} className="om-card" onClick={() => navigate(ROUTES.place(p.id))}>
              <span className="om-card__thumb" aria-hidden="true">
                <CategoryIcon code={p.category.code} size={26} />
              </span>
              <span className="om-card__body">
                <span className="om-card__name">{p.name}</span>
                <span className="om-card__row">
                  <CategoryChip code={p.category.code} />
                  <span className="om-card__dist num">{formatDistance(p.distanceMeters)}</span>
                </span>
              </span>
            </button>
          )) : places.state.status === "success" && places.state.data.slice(0, 6).map((p) => (
            <button key={p.id} className="om-card" onClick={() => navigate(ROUTES.place(p.id))}>
              <span className="om-card__thumb" aria-hidden="true">
                <CategoryIcon code={p.category.code} size={26} />
              </span>
              <span className="om-card__body">
                <span className="om-card__name">{p.name}</span>
                <span className="om-card__row">
                  <CategoryChip code={p.category.code} />
                </span>
                <span className="om-card__row">
                  <VerificationBadge status={p.verificationStatus} lastVerifiedAt={p.lastVerifiedAt} />
                </span>
              </span>
            </button>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, more, state, error, onRetry, children, isEmpty, empty }: {
  title: string;
  more?: { label: string; to: string };
  state: "idle" | "loading" | "success" | "error";
  /** 왜 실패했는지. 없으면 사람은 와이파이 문제인지 서버 문제인지 알 수 없다. */
  error?: Error;
  onRetry: () => void;
  children: React.ReactNode;
  isEmpty: boolean;
  empty: React.ReactNode;
}) {
  if (state === "error") {
    /*
     * 비었을 때 스스로 숨는 구역(`empty === null`)은 실패했을 때도 숨는다.
     * 서버를 멈춰 놓고 보니 "이번 주 이벤트" 와 "곧 종료" 가 **같은 요청 하나**로
     * 그려지는데 실패 문구를 나란히 두 번 내밀었다 — 두 군데가 고장 난 것처럼
     * 보인다. 이 구역은 위 구역의 다른 얼굴이므로 한 번만 말한다.
     */
    if (empty === null) return null;
    return (
      <section className="om-section">
        <h2 className="om-section__title">{title}</h2>
        <ErrorState level={3} title="불러오지 못했어요" body={error?.message} onRetry={onRetry} />
      </section>
    );
  }
  if (state === "loading" || state === "idle") {
    return (
      <section className="om-section">
        <h2 className="om-section__title">{title}</h2>
        <Skeleton lines={2} />
      </section>
    );
  }
  if (isEmpty && empty === null) return null;      // 조용히 숨기는 섹션
  return (
    <section className="om-section">
      <div className="om-section__head">
        <h2 className="om-section__title">{title}</h2>
        {more ? <button className="om-more" onClick={() => navigate(more.to)}>{more.label}</button> : null}
      </div>
      {isEmpty ? empty : children}
    </section>
  );
}
